<script>
import baseMixin from '@/mixins/baseMixin'
import { mapState } from 'vuex'
import HttpStatusCodes from '@/constants/api/HttpStatusCodes'
import config from '@/common/config'
import DisputeDialogBookingDetails from '@/components/disputes/disputes-dialog/dispute-dialog-booking-details.vue'
import ExpansionPanels from '@/components/general/expansion-panels.vue'
import DisputeDialogClientInfoCard from './dispute-dialog-client-info-card.vue'
import DisputeDialogCandidateInfoCard from './dispute-dialog-candidate-info-card.vue'
import DisputeDialogCandidateReasonsCard from './disputes-dialog-candidate-reasons-card.vue'

export default {
  components: {
    DisputeDialogBookingDetails,
    DisputeDialogClientInfoCard,
    DisputeDialogCandidateInfoCard,
    DisputeDialogCandidateReasonsCard,
    ExpansionPanels,
  },
  mixins: [baseMixin],
  data: () => ({
    onMessageListener: null,
  }),
  computed: {
    ...mapState({
      taskType: (state) => state.disputes.flyout.task.type,
      formUrl: (state) => state.disputes.flyout.task.formUrl,
      taskId: (state) => state.disputes.flyout.task.id,
      status: (state) => state.disputes.flyout.task.status,
      timesheet: (state) => state.disputes.flyout.timesheet?.bookingId,
    }),
    formAlreadySubmitted() {
      return this.status === HttpStatusCodes.NoContent
    },
  },
  beforeDestroy() {
    if (this.onMessageListener) {
      removeEventListener('message', this.handleOnMessage)
      this.onMessageListener = null
    }
  },
  async mounted() {
    this.setupForms2MessageListener()

    if (!this.timesheet) {
      await this.$store.dispatch('disputes/getDisputeDetailsById')
    }
  },
  methods: {
    exitTasksDialog() {
      this.$emit('refreshTaskList')
      this.$store.commit('disputes/RESET_FLYOUT')
    },
    localSetTaskCompleted() {
      const completedTasks = this.parseJson(
        sessionStorage.getItem('completedTasks'),
        []
      )

      if (!completedTasks.includes(this.taskId)) {
        completedTasks.push(this.taskId)
      }

      sessionStorage.setItem('completedTasks', JSON.stringify(completedTasks))
    },
    setupForms2MessageListener() {
      if (this.onMessageListener) return
      this.onMessageListener = this.handleOnMessage.bind(this)
      addEventListener('message', this.handleOnMessage)
    },
    async handleOnMessage(event) {
      const allowedOrigins = config.get('forms2.allowedOrigins').split(',')

      if (
        !allowedOrigins.some(
          (origin) => event.origin === origin || origin === '*'
        )
      )
        return

      if (
        Object.hasOwn(event.data, 'submission_type') &&
        event.data?.submitted === 'true'
      ) {
        await this.localSetTaskCompleted()
        this.exitTasksDialog()
      }
    },
  },
}
</script>

<template>
  <div class="embedded-form-iframe">
    <ExpansionPanels
      v-if="timesheet"
      :flat="true"
      :header-class="'px-0'"
      class="mb-4"
    >
      <template v-slot:header-actions="{ open }">
        <div class="d-flex flex-column align-center">
          <v-icon size="30"> mdi-chevron-down </v-icon>
          <p :class="{ 'opacity-0': open }">{{
            $t('dispute.task.detailsExpandIconCaption')
          }}</p>
        </div>
      </template>
      <template v-slot:header-content>
        <DisputeDialogBookingDetails details-only class="mr-4" />
      </template>
      <template v-slot:panel-content>
        <v-row>
          <v-col cols="12" md="6">
            <DisputeDialogClientInfoCard />
          </v-col>
          <v-col cols="12" md="6">
            <DisputeDialogCandidateInfoCard />
          </v-col>
        </v-row>
      </template>
    </ExpansionPanels>
    <DisputeDialogCandidateReasonsCard />

    <iframe
      v-if="formUrl && !formAlreadySubmitted"
      :src="formUrl"
      class="fill-height w-full"
      frameborder="0"
    ></iframe>
    <div
      v-if="formAlreadySubmitted"
      data-testid="form-already-submitted"
      class="d-flex flex-column align-center mt-10"
    >
      <v-icon size="120" color="primary">mdi-check-circle-outline</v-icon>
      <p class="text-h6 text-center mt-6">{{
        $t('dispute.task.alreadySubmittedText')
      }}</p>

      <v-btn
        width="240px"
        class="mt-4 py-6"
        color="primary"
        @click="exitTasksDialog"
        >{{ $t('dispute.task.taskButtonClose') }}</v-btn
      >
      <p v-if="taskId" class="text-subtitle text-center mt-4">{{ taskId }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.embedded-form-iframe {
  width: 100%;
  height: 100%;
}

.w-full {
  width: 100%;
}

.form-title-header {
  background-color: var(--v-blue-base);
  border-radius: 5px 5px 0 0;
}

.v-card__text:has(> .embedded-form-iframe) {
  overflow: hidden !important;
}
</style>
